import { supabase } from './supabase';
import { gatewayAuthHeaders } from './gateway-auth';
import type {
  AlarmSite,
  AlarmPanel,
  AlarmPanelWithSite,
  AlarmZone,
  AlarmEvent,
  AlarmEventWithRelations,
} from '@asi/shared';

const GATEWAY_URL = import.meta.env.VITE_GATEWAY_URL as string | undefined;

// ─── Gateway Proxy ────────────────────────────────────────────

async function callGateway<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  if (!GATEWAY_URL) {
    throw new Error('Gateway URL not configured');
  }

  const url = `${GATEWAY_URL}${path}`;
  const init: RequestInit = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...gatewayAuthHeaders(),
      ...options.headers,
    },
  };

  const res = await fetch(url, init);
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as Record<string, string>;
    throw new Error(err.error ?? `Gateway error ${res.status}`);
  }
  return res.json() as Promise<T>;
}

// ─── HPP API Calls ────────────────────────────────────────────

/** Get HPP connection status */
export async function getHppStatus(): Promise<{
  configured: boolean;
  authenticated: boolean;
  areaDomain?: string;
}> {
  return callGateway('/api/hpp/status');
}

/** List sites from HPP (paginated) */
export async function listHppSites(page = 1, pageSize = 50): Promise<{
  sites: Array<{
    id: string;
    name: string;
    address: string;
    latitude: number;
    longitude: number;
  }>;
  total: number;
}> {
  return callGateway(`/api/hpp/sites?page=${page}&pageSize=${pageSize}`);
}

/** List devices from HPP (paginated) */
export async function listHppDevices(page = 1, pageSize = 50, siteId?: string): Promise<{
  devices: Array<{
    id: string;
    deviceSerial: string;
    deviceName: string;
    deviceType: string;
    deviceCategory: number;
    deviceOnlineStatus: number;
    deviceVersion: string;
    siteID: string;
    siteName: string;
    isSubscribed?: boolean;
  }>;
  total: number;
  totalPage: number;
}> {
  let url = `/api/hpp/devices?page=${page}&pageSize=${pageSize}`;
  if (siteId) url += `&siteId=${siteId}`;
  return callGateway(url);
}

/** Get the live zone → device → area list for a panel directly from ISAPI (ground truth). */
export async function getPanelZones(serial: string): Promise<{
  zones: Array<{
    zoneNo: number | null;
    name: string | null;
    status: string | null;
    subSystemNo: number | null;
    tamperEvident: boolean;
    bypassed: boolean;
    alarm: boolean;
    detectorType: string | null;
    zoneType: string | null;
    zoneAttrib: string | null;
    chargeValue: number | null;
    signal: number | null;
  }>;
  total: number;
}> {
  return callGateway(`/api/hpp/panels/${serial}/zones`);
}

// ─── Zonificación (device/zone inventory, auto-synced from arc_events) ───────

/** List zones known for a panel (auto-populated from incoming CID events). */
export async function fetchAlarmZones(panelId: string): Promise<AlarmZone[]> {
  const { data, error } = await supabase
    .from('alarm_zones')
    .select('*')
    .eq('panel_id', panelId)
    .order('zone_number', { ascending: true });

  if (error) throw error;
  return (data ?? []) as AlarmZone[];
}

/** Manually correct a zone's name and/or type. Marks the field as operator-owned
 *  so future incoming events never silently overwrite the correction. */
export async function updateAlarmZone(
  zoneId: string,
  updates: { name?: string; type?: AlarmZone['type'] },
): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (updates.name !== undefined) {
    payload.name = updates.name;
    payload.name_source = 'manual';
  }
  if (updates.type !== undefined) {
    payload.type = updates.type;
    payload.type_source = 'manual';
  }
  if (Object.keys(payload).length === 0) return;

  const { error } = await supabase.from('alarm_zones').update(payload).eq('id', zoneId);
  if (error) throw error;
}

/** Remove a zone row (e.g. a device that was physically uninstalled/deleted from the panel). */
export async function deleteAlarmZone(zoneId: string): Promise<void> {
  const { error } = await supabase.from('alarm_zones').delete().eq('id', zoneId);
  if (error) throw error;
}

/** Fallback only: classifies from Hikvision's `zoneType` (Instant/Delay/Follower/...)
 *  when no `detectorType` is available at all. Prefer `classifyZoneFromIsapi`. */
function mapHikvisionZoneType(zoneType: string | null): AlarmZone['type'] {
  if (!zoneType) return 'OTHER';
  const t = zoneType.toLowerCase();
  if (t.includes('panic') || t.includes('emergency') || t.includes('duress')) return 'PANIC';
  if (t.includes('fire') || t.includes('smoke')) return 'FIRE';
  if (t.includes('medical')) return 'MEDICAL';
  if (t.includes('perimeter')) return 'PERIMETER';
  if (t.includes('interior') || t.includes('follower')) return 'INTERIOR';
  if (t.includes('instant')) return 'INSTANT';
  if (t.includes('delay')) return 'DELAY';
  if (t.includes('24')) return '24HR';
  return 'OTHER';
}

/**
 * Classifies a zone from ISAPI data. `detectorType` (the physical device — PIR,
 * magnetic contact, smoke detector, panic button...) is the PRIMARY signal
 * since it directly says what the sensor IS. `zoneType` (Instant/Delay/
 * Follower/...) describes the panel's ARMING BEHAVIOR for that zone, not the
 * device family — using it as the primary classifier made every burglary
 * sensor (motion/magnetic) come back as INSTANT/DELAY instead of
 * PERIMETER/INTERIOR, which broke the "¿coincide el nombre con el tipo?"
 * heuristic for every single zone.
 */
function classifyZoneFromIsapi(detectorType: string | null, zoneType: string | null): AlarmZone['type'] {
  const d = (detectorType ?? '').toLowerCase();
  if (!d) return mapHikvisionZoneType(zoneType);

  if (d.includes('smoke') || d.includes('heat') || d.includes('fire')) return 'FIRE';
  if (d.includes('panic') || d.includes('emergency') || d.includes('duress')) return 'PANIC';
  if (d.includes('medical')) return 'MEDICAL';

  const isBurglaryDevice = ['passiveinfrared', 'pir', 'curtain', 'dualtech', 'shock', 'magnetic', 'contact']
    .some((k) => d.includes(k));
  if (isBurglaryDevice) {
    const t = (zoneType ?? '').toLowerCase();
    if (t.includes('perimeter') || t.includes('instant')) return 'PERIMETER';
    return 'INTERIOR'; // covers follower/delay/interior — and is the safer default when ambiguous
  }

  // Gas/water/glassbreak/siren/keypad/repeater have no dedicated category yet.
  return 'OTHER';
}

/**
 * Sync zones for a panel directly from the live ISAPI zone list (ground truth):
 * real device type (`detectorType`), real zone type (`zoneType`), and real
 * area/partition (`subSystemNo`) — none of which are reliable from CID events
 * alone. Zones no longer present on the panel are flagged `removed_from_panel`
 * instead of being silently kept around forever.
 */
export async function syncZonesFromPanel(panelId: string, serial: string): Promise<AlarmZone[]> {
  const { zones: liveZones } = await getPanelZones(serial);

  const seenZoneNumbers = new Set<number>();
  const now = new Date().toISOString();

  for (const z of liveZones) {
    if (z.zoneNo == null) continue;
    seenZoneNumbers.add(z.zoneNo);

    const existing = await supabase
      .from('alarm_zones')
      .select('id, type_source, name_source')
      .eq('panel_id', panelId)
      .eq('zone_number', z.zoneNo)
      .maybeSingle();

    const payload: Record<string, unknown> = {
      panel_id: panelId,
      zone_number: z.zoneNo,
      detector_type: z.detectorType,
      zone_type_raw: z.zoneType,
      subsystem_no: z.subSystemNo,
      zone_attrib: z.zoneAttrib,
      synced_from_panel_at: now,
      removed_from_panel: false,
    };
    if (existing.data?.name_source !== 'manual') payload.name = z.name ?? `Zona ${z.zoneNo}`;
    if (existing.data?.type_source !== 'manual') payload.type = classifyZoneFromIsapi(z.detectorType, z.zoneType);

    await supabase.from('alarm_zones').upsert(payload, { onConflict: 'panel_id,zone_number' });
  }

  // Anything previously known for this panel but absent from the live list was removed.
  const { data: allZones } = await supabase.from('alarm_zones').select('id, zone_number').eq('panel_id', panelId);
  const staleIds = (allZones ?? []).filter((z) => !seenZoneNumbers.has(z.zone_number)).map((z) => z.id);
  if (staleIds.length > 0) {
    await supabase.from('alarm_zones').update({ removed_from_panel: true }).in('id', staleIds);
  }

  return fetchAlarmZones(panelId);
}

// ─── Site floor plan ("croquis de instalación") ──────────────────────────────

const FLOOR_PLAN_BUCKET = 'site-floorplans';

/** Upload/replace the as-built installation floor plan for a site. */
export async function uploadSiteFloorPlan(siteId: string, file: File): Promise<string> {
  const ext = file.name.split('.').pop() ?? 'png';
  const path = `${siteId}/${Date.now()}-croquis.${ext}`;

  const { error: uploadError } = await supabase.storage
    .from(FLOOR_PLAN_BUCKET)
    .upload(path, file, { contentType: file.type, upsert: false });
  if (uploadError) throw uploadError;

  const { error: updateError } = await supabase
    .from('alarm_sites')
    .update({ floor_plan_path: path, floor_plan_uploaded_at: new Date().toISOString() })
    .eq('id', siteId);
  if (updateError) throw updateError;

  return path;
}

/** Remove the current floor plan (storage object + site reference). */
export async function deleteSiteFloorPlan(siteId: string, path: string): Promise<void> {
  await supabase.storage.from(FLOOR_PLAN_BUCKET).remove([path]);
  const { error } = await supabase
    .from('alarm_sites')
    .update({ floor_plan_path: null, floor_plan_uploaded_at: null })
    .eq('id', siteId);
  if (error) throw error;
}

/** Get a temporary signed URL to view/download the floor plan (private bucket). */
export async function getSiteFloorPlanUrl(path: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from(FLOOR_PLAN_BUCKET)
    .createSignedUrl(path, 3600);
  if (error) throw error;
  return data.signedUrl;
}

/** Arm a panel */
export async function armPanel(serial: string, mode: 'away' | 'stay' | 'instant' = 'away'): Promise<{
  success: boolean;
  mode: string;
}> {
  return callGateway(`/api/hpp/panels/${serial}/arm`, {
    method: 'POST',
    body: JSON.stringify({ mode }),
  });
}

/** Disarm a panel */
export async function disarmPanel(serial: string): Promise<{ success: boolean }> {
  return callGateway(`/api/hpp/panels/${serial}/disarm`, {
    method: 'POST',
  });
}

/** Fetch + refresh subsystems from live ISAPI state, persisted to DB */
export async function getSubsystems(serial: string): Promise<{ id: number; is_armed: boolean; arm_mode: string | null; name?: string }[]> {
  // Use the edge function so the result is persisted to DB and triggers Realtime updates
  const { data, error } = await supabase.functions.invoke('arm-panel', {
    body: { serial, action: 'subsystems' },
  });
  if (error) {
    // Fallback to gateway on error (read-only, no DB update)
    const res = await callGateway(`/api/hpp/panels/${serial}/subsystems`) as { subsystems?: unknown[] };
    return (res.subsystems ?? []) as { id: number; is_armed: boolean; arm_mode: string | null; name?: string }[];
  }
  return ((data as any)?.subsystems ?? []) as { id: number; is_armed: boolean; arm_mode: string | null; name?: string }[];
}

/** Arm a specific subsystem/area, optionally bypassing faulted zones */
export async function armSubsystem(serial: string, subsysId: number, mode: 'away' | 'stay' = 'away', bypassFaulted = false): Promise<{ success: boolean }> {
  const { data, error } = await supabase.functions.invoke('arm-panel', {
    body: { serial, action: 'arm', mode, subsysId, ...(bypassFaulted ? { bypassFaulted: true } : {}) },
  });
  if (error) {
    let msg = error.message;
    try { const ctx = await (error as any).context?.json?.(); msg = ctx?.error ?? msg; } catch {}
    throw new Error(msg ?? 'Error al armar área');
  }
  return { success: true, ...(data as object) };
}

/** Audible panic: the panel's one-key alarm (siren). It reaches monitoring as CID 1129. */
export async function triggerPanic(serial: string): Promise<void> {
  const { data, error } = await supabase.functions.invoke('arm-panel', {
    body: { serial, action: 'panic' },
  });
  if (error) {
    let msg = error.message;
    try { const ctx = await (error as any).context?.json?.(); msg = ctx?.error ?? msg; } catch {}
    throw new Error(msg ?? 'No se pudo activar la alarma de pánico');
  }
  if ((data as { error?: string } | null)?.error) throw new Error((data as { error: string }).error);
}

/** Disarm a specific subsystem/area */
export async function disarmSubsystem(serial: string, subsysId: number): Promise<{ success: boolean }> {
  const { data, error } = await supabase.functions.invoke('arm-panel', {
    body: { serial, action: 'disarm', subsysId },
  });
  if (error) {
    let msg = error.message;
    try { const ctx = await (error as any).context?.json?.(); msg = ctx?.error ?? msg; } catch {}
    throw new Error(msg ?? 'Error al desarmar área');
  }
  return { success: true, ...(data as object) };
}

/**
 * Save custom names for subsystems.
 * Merges the provided names into the existing `subsystems` JSONB column.
 * names: { [subsysId]: label }  e.g. { 1: 'Perimetral', 2: 'Interna' }
 */
export async function saveSubsystemNames(panelId: string, names: Record<number, string>): Promise<void> {
  const { data: panel } = await supabase
    .from('alarm_panels')
    .select('subsystems')
    .eq('id', panelId)
    .single();

  const current: { id: number; is_armed: boolean; arm_mode: string | null; name?: string }[] =
    Array.isArray(panel?.subsystems) ? (panel!.subsystems as typeof current) : [];

  // Ensure all named subsystems exist in the array
  const allIds = new Set([...current.map((s) => s.id), ...Object.keys(names).map(Number)]);
  const updated = [...allIds].sort().map((id) => {
    const existing = current.find((s) => s.id === id) ?? { id, is_armed: false, arm_mode: null };
    return { ...existing, name: names[id] ?? existing.name };
  });

  const { error } = await supabase
    .from('alarm_panels')
    .update({ subsystems: updated })
    .eq('id', panelId);

  if (error) throw new Error(error.message);
}

// ─── Intelbras TCP Direct Control (port 9009, native 0xe7 protocol) ───────────

export interface IntelbrasStatus {
  connected: boolean;
  authenticated: boolean;
  armed: boolean;
  alarm: boolean;
  alarm_memory: boolean;
  raw?: string;
}

export interface IntelbrasControlResult {
  success: boolean;
  status?: IntelbrasStatus;
  error?: string;
}

export function getIntelbrasCloudStatus(panelId: string): Promise<IntelbrasStatus> {
  return callGateway(`/api/intelbras/${panelId}/cloud/status`);
}

export function armIntelbrasCloudPanel(panelId: string): Promise<IntelbrasControlResult> {
  return callGateway(`/api/intelbras/${panelId}/cloud/arm`, { method: 'POST' });
}

export function disarmIntelbrasCloudPanel(panelId: string): Promise<IntelbrasControlResult> {
  return callGateway(`/api/intelbras/${panelId}/cloud/disarm`, { method: 'POST' });
}

export function getIntelbrasStatus(panelId: string): Promise<IntelbrasStatus> {
  return callGateway(`/api/intelbras/${panelId}/status`);
}

export function armIntelbrasPanel(panelId: string): Promise<IntelbrasControlResult> {
  return callGateway(`/api/intelbras/${panelId}/arm`, { method: 'POST' });
}

export function disarmIntelbrasPanel(panelId: string): Promise<IntelbrasControlResult> {
  return callGateway(`/api/intelbras/${panelId}/disarm`, { method: 'POST' });
}

export function toggleIntelbrasPanel(panelId: string): Promise<IntelbrasControlResult> {
  return callGateway(`/api/intelbras/${panelId}/arm`, { method: 'POST' });
}

/** Get panel status */
export async function getPanelStatus(serial: string): Promise<{
  deviceInfo: unknown;
  armStatus: unknown;
}> {
  return callGateway(`/api/hpp/panels/${serial}/status`);
}

/** Subscribe to alarm message queue */
export async function subscribeToAlarms(subscribe = true, mode: 'all' | 'list' = 'all'): Promise<{
  success: boolean;
  subscribed: boolean;
  mode: string;
}> {
  return callGateway('/api/hpp/mq/subscribe', {
    method: 'POST',
    body: JSON.stringify({ subscribe, mode }),
  });
}

/** Poll alarm messages */
export async function pollAlarms(timeout = 20000): Promise<{
  batchId: string | null;
  list: Array<{
    msgType: string;
    alarmData: Record<string, unknown>;
  }>;
}> {
  return callGateway('/api/hpp/mq/poll', {
    method: 'POST',
    body: JSON.stringify({ timeout }),
  });
}

/** Confirm alarm batch receipt */
export async function confirmAlarmBatch(batchId: string): Promise<{ success: boolean }> {
  return callGateway('/api/hpp/mq/confirm', {
    method: 'POST',
    body: JSON.stringify({ batchId }),
  });
}

// ─── Database Operations ──────────────────────────────────────

/** Fetch all sites from database */
export async function fetchSites(): Promise<AlarmSite[]> {
  const { data, error } = await supabase
    .from('alarm_sites')
    .select('id, name, address, city, is_active, service_level, account_number, contact_name, contact_phone, contact_email, hpp_site_id')
    .order('name');

  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as AlarmSite[];
}

/** Fetch all panels from database with site info */
export async function fetchPanels(): Promise<AlarmPanelWithSite[]> {
  const { data, error } = await supabase
    .from('alarm_panels')
    .select(`
      *,
      site:alarm_sites(*, account:accounts(id, name))
    `)
    .order('name');

  if (error) throw new Error(error.message);
  return data ?? [];
}

/** Fetch panels for a specific site */
export async function fetchPanelsBySite(siteId: string): Promise<AlarmPanel[]> {
  const { data, error } = await supabase
    .from('alarm_panels')
    .select('*')
    .eq('site_id', siteId)
    .order('name');

  if (error) throw new Error(error.message);
  return data ?? [];
}

/** Fetch zones for a panel */
export async function fetchZones(panelId: string): Promise<AlarmZone[]> {
  const { data, error } = await supabase
    .from('alarm_zones')
    .select('*')
    .eq('panel_id', panelId)
    .order('zone_number');

  if (error) throw new Error(error.message);
  return data ?? [];
}

/** Fetch recent events with relations */
export async function fetchRecentEvents(limit = 100): Promise<AlarmEventWithRelations[]> {
  const { data, error } = await supabase
    .from('alarm_events')
    .select(`
      *,
      panel:alarm_panels(*),
      zone:alarm_zones(*),
      event_type:alarm_event_types(*)
    `)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw new Error(error.message);
  return data ?? [];
}

/** Fetch unacknowledged critical/alarm events */
export async function fetchUnacknowledgedEvents(): Promise<AlarmEventWithRelations[]> {
  const { data, error } = await supabase
    .from('alarm_events')
    .select(`
      *,
      panel:alarm_panels(*),
      zone:alarm_zones(*),
      event_type:alarm_event_types(*)
    `)
    .is('acknowledged_at', null)
    .in('severity', ['ALARM', 'CRITICAL'])
    .order('created_at', { ascending: false });

  if (error) throw new Error(error.message);
  return data ?? [];
}

/** Acknowledge an event */
export async function acknowledgeEvent(eventId: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { error } = await supabase
    .from('alarm_events')
    .update({
      acknowledged_at: new Date().toISOString(),
      acknowledged_by: user.id,
    })
    .eq('id', eventId);

  if (error) throw new Error(error.message);
}

/** Count events by severity */
export async function countEventsBySeverity(): Promise<Record<string, number>> {
  const { data, error } = await supabase
    .from('alarm_events')
    .select('severity')
    .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString());

  if (error) throw new Error(error.message);

  const counts: Record<string, number> = { INFO: 0, WARNING: 0, ALARM: 0, CRITICAL: 0 };
  for (const event of data ?? []) {
    counts[event.severity] = (counts[event.severity] ?? 0) + 1;
  }
  return counts;
}

/** Count panels by status */
export async function countPanelsByStatus(): Promise<Record<string, number>> {
  const { data, error } = await supabase
    .from('alarm_panels')
    .select('status');

  if (error) throw new Error(error.message);

  const counts: Record<string, number> = { ONLINE: 0, OFFLINE: 0, ALARM: 0, FAULT: 0 };
  for (const panel of data ?? []) {
    counts[panel.status] = (counts[panel.status] ?? 0) + 1;
  }
  return counts;
}

// ─── Sync Operations ──────────────────────────────────────────

export interface HppSyncResult {
  startedAt: string;
  durationMs: number;
  sites: { total: number; inserted: number; updated: number };
  panels: { total: number; inserted: number; updated: number; unchanged: number; errors: number };
  resubscribed: number;
}

/**
 * Run the HPP → database sync of sites and panels on the gateway.
 * The gateway also runs it automatically every few minutes; this is the manual fallback.
 */
export async function runHppSync(): Promise<HppSyncResult> {
  return callGateway<HppSyncResult>('/api/hpp/sync', { method: 'POST' });
}

/** Last automatic/manual sync result (for the "última sincronización" label). */
export async function fetchHppSyncStatus(): Promise<{
  running: boolean;
  lastResult: HppSyncResult | null;
  lastError: string | null;
}> {
  return callGateway('/api/hpp/sync/status');
}

/**
 * Refresh partition (subsystem) arm states of online panels in the background.
 * Each call hits the physical panel via ISAPI and can take seconds, so it is fire-and-forget
 * and only triggered by the manual sync, not by the periodic one.
 */
export function refreshPanelSubsystems(serials: string[]): void {
  if (serials.length === 0) return;
  Promise.allSettled(
    serials.map((serial) =>
      getSubsystems(serial).then(async (subs) => {
        if (subs.length === 0) return;
        await supabase.from('alarm_panels').update({ subsystems: subs }).eq('serial_number', serial);
      }).catch((e: unknown) => {
        console.warn(`[refreshPanelSubsystems] ${serial}:`, e);
      }),
    ),
  );
}

// ─── Realtime Subscriptions ───────────────────────────────────

/** Subscribe to alarm events in realtime */
export function subscribeToAlarmEvents(
  callback: (event: AlarmEvent) => void,
): () => void {
  const channel = supabase
    .channel(`alarm_events_realtime-${Date.now()}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'alarm_events',
      },
      (payload) => {
        callback(payload.new as AlarmEvent);
      },
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

/** Subscribe to panel changes (inserts and updates) */
export function subscribeToPanelChanges(
  onInsert: (panel: AlarmPanel) => void,
  onUpdate: (panel: AlarmPanel) => void,
): () => void {
  const channel = supabase
    .channel(`alarm_panels_realtime-${Date.now()}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'alarm_panels',
      },
      (payload) => {
        onInsert(payload.new as AlarmPanel);
      },
    )
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'alarm_panels',
      },
      (payload) => {
        onUpdate(payload.new as AlarmPanel);
      },
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

/** Fires when any alarm_sites row changes account_id (claimed via QR, released,
 *  manually linked/unlinked) — the panels list embeds site.account_id, which a
 *  plain alarm_panels subscription can't see change on its own. Callers should
 *  just refetch panels; this is infrequent enough that a full refetch is fine. */
export function subscribeToSiteAccountChanges(onChange: () => void): () => void {
  const channel = supabase
    .channel(`alarm_sites_account_realtime-${Date.now()}`)
    .on(
      'postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'alarm_sites' },
      () => onChange(),
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

/** Fetch panels not yet assigned to any account:
 *  panels with site_id IS NULL, OR whose site has no account assigned (orphaned
 *  after account deletion). Uses JOIN + client-side filter to match the
 *  AlarmsPage "Sin cuenta" tab logic exactly. */
export async function fetchUnassignedPanels(): Promise<AlarmPanel[]> {
  const { data, error } = await supabase
    .from('alarm_panels')
    .select('*, site:alarm_sites(id, account_id)')
    .order('name');
  if (error) throw new Error(error.message);
  type WithSite = AlarmPanel & { site: { id: string; account_id: string | null } | null };
  return ((data as unknown as WithSite[]) ?? [])
    .filter((p) => !p.site || !p.site.account_id)
    .map(({ site: _site, ...panel }) => panel as AlarmPanel);
}

/** Link (or unlink) a panel to a site. Pass null to remove the link. */
export async function linkPanelToSite(panelId: string, siteId: string | null): Promise<void> {
  const { error } = await supabase
    .from('alarm_panels')
    .update({ site_id: siteId })
    .eq('id', panelId);
  if (error) throw new Error(error.message);
}

// ─── Panel Disable / Enable ───────────────────────────────────

export async function disablePanel(
  panelId: string,
  opts: { reason?: string; siteId?: string | null; accountId?: string | null; deviceName: string }
): Promise<void> {
  const now = new Date().toISOString();
  const { error } = await supabase
    .from('alarm_panels')
    .update({ is_disabled: true, disabled_at: now, disabled_reason: opts.reason ?? null })
    .eq('id', panelId);
  if (error) throw new Error(error.message);

  await supabase.from('device_deactivation_log').insert({
    device_type: 'panel',
    device_id: panelId,
    device_name: opts.deviceName,
    site_id: opts.siteId ?? null,
    account_id: opts.accountId ?? null,
    action: 'disabled',
    reason: opts.reason ?? null,
  });
}

export async function enablePanel(
  panelId: string,
  opts: { deviceName: string; siteId?: string | null; accountId?: string | null }
): Promise<void> {
  const { error } = await supabase
    .from('alarm_panels')
    .update({ is_disabled: false, disabled_at: null, disabled_reason: null })
    .eq('id', panelId);
  if (error) throw new Error(error.message);

  await supabase.from('device_deactivation_log').insert({
    device_type: 'panel',
    device_id: panelId,
    device_name: opts.deviceName,
    site_id: opts.siteId ?? null,
    account_id: opts.accountId ?? null,
    action: 'enabled',
    reason: null,
  });
}

/** Subscribe to panel status changes (legacy, update only) */
export function subscribeToPanelStatus(
  callback: (panel: AlarmPanel) => void,
): () => void {
  const channel = supabase
    .channel('alarm_panels_status')
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'alarm_panels',
      },
      (payload) => {
        callback(payload.new as AlarmPanel);
      },
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}
